import { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import MapPicker from "../components/ui/MapPicker";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  Eye,
  Server,
  Plug,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

export default function AdminNAPBoxes() {
  const [napBoxes, setNapBoxes] = useState([]);
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [portsDialogOpen, setPortsDialogOpen] = useState(false);
  const [editingBox, setEditingBox] = useState(null);
  const [selectedBox, setSelectedBox] = useState(null);
  const [ports, setPorts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [serverFilter, setServerFilter] = useState("all");
  const [formData, setFormData] = useState({
    name: "",
    location: "",
    latitude: "",
    longitude: "",
    server_id: "",
    total_ports: "8",
    status: "Active",
    description: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const [boxesRes, serversRes] = await Promise.all([
        axios.get(`${API}/nap-boxes`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/servers`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setNapBoxes(boxesRes.data);
      setServers(serversRes.data);
    } catch (error) {
      toast.error("Failed to fetch data asdfasdfasd");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const submitData = {
        ...formData,
        total_ports: parseInt(formData.total_ports),
      };

      if (editingBox) {
        // Don't send total_ports on update
        const { total_ports, ...updateData } = submitData;
        await axios.put(`${API}/nap-boxes/${editingBox.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("NAP Box updated successfully");
      } else {
        await axios.post(`${API}/nap-boxes`, submitData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success(`NAP Box created with ${submitData.total_ports} ports!`);
      }
      setOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save NAP Box");
    }
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this NAP Box? All ports will be deleted.",
      )
    )
      return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/nap-boxes/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("NAP Box deleted successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete NAP Box");
    }
  };

  const viewPorts = async (box) => {
    setSelectedBox(box);

    setFormData({
      name: box.name,
      location: box.location || 121.08,
      latitude: box.latitude || 14.55,
      longitude: box.longitude,
      server_id: box.server_id,
      total_ports: (box.total_ports || 8).toString(),
      status: box.status,
      description: box.description || "",
    });

    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/nap-boxes/${box.id}/ports`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPorts(res.data.ports || []);
      setPortsDialogOpen(true);
    } catch (error) {
      toast.error("Failed to fetch ports");
    }
  };

  const updatePortStatus = async (portId, newStatus) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/ports/${portId}`,
        { status: newStatus },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Port status updated");
      // Refresh ports
      if (selectedBox) {
        viewPorts(selectedBox);
      }
      fetchData();
    } catch (error) {
      toast.error("Failed to update port");
    }
  };

  const openEditDialog = (box) => {
    setEditingBox(box);
    setFormData({
      name: box.name,
      location: box.location || 121.08,
      latitude: box.latitude || 14.55,
      longitude: box.longitude,
      server_id: box.server_id,
      total_ports: (box.total_ports || 8).toString(),
      status: box.status,
      description: box.description || "",
    });
    setOpen(true);
  };

  const resetForm = () => {
    setEditingBox(null);
    setFormData({
      name: "",
      location: "",
      latitude: 0,
      longitude: 0,
      server_id: "",
      total_ports: "8",
      status: "Active",
      description: "",
    });
  };

  const getStatusBadge = (status) => {
    const colors = {
      Active: "bg-emerald-100 text-emerald-700 border-emerald-300",
      Maintenance: "bg-amber-100 text-amber-700 border-amber-300",
      Inactive: "bg-slate-100 text-slate-700 border-slate-300",
    };
    return (
      <span
        className={`px-3 py-1 text-xs font-semibold border rounded-full ${colors[status] || colors.Active}`}
      >
        {status}
      </span>
    );
  };

  const getPortStatusBadge = (status) => {
    const colors = {
      Available: "bg-emerald-100 text-emerald-700",
      Occupied: "bg-blue-100 text-blue-700",
      Reserved: "bg-amber-100 text-amber-700",
      Faulty: "bg-red-100 text-red-700",
    };
    return (
      <span
        className={`px-2 py-1 text-xs font-semibold rounded ${colors[status] || colors.Available}`}
      >
        {status}
      </span>
    );
  };

  const getServerName = (serverId) => {
    const server = servers.find((s) => s.id === serverId);
    return server ? server.name : "Unknown";
  };

  const filteredNapBoxes = napBoxes.filter((box) => {
    const matchesSearch =
      box.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      box.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
      getServerName(box.server_id)
        .toLowerCase()
        .includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === "all" || box.status === statusFilter;
    const matchesServer =
      serverFilter === "all" || box.server_id === serverFilter;

    return matchesSearch && matchesStatus && matchesServer;
  });

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          Loading...
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="h-[calc(100vh-64px)] flex flex-col overflow-hidden p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1
              className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1"
              data-testid="napboxes-title"
            >
              NAP Box Management
            </h1>
            <p className="text-sm text-slate-600">
              Manage Network Access Point boxes and port assignments
            </p>
          </div>
          <Dialog
            open={open}
            onOpenChange={(val) => {
              setOpen(val);
              if (!val) resetForm();
            }}
          >
            <DialogTrigger asChild>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white gap-2 rounded-lg shadow-lg shadow-blue-600/30"
                data-testid="add-napbox-btn"
              >
                <Plus className="w-4 h-4" />
                Add NAP Box
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
              <DialogHeader className="top-0 z-20 bg-white px-6 py-4 border-b">
                <DialogTitle className="text-xl font-bold">
                  {editingBox ? "Edit NAP Box" : "Add New NAP Box"}
                </DialogTitle>
              </DialogHeader>
              <div className="px-6 pb-6">
                <form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                  data-testid="napbox-form"
                >
                  <div className="relative">
                    {loading && (
                      <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-50 flex items-center justify-center rounded-lg">
                        <div className="flex items-center gap-2 text-blue-700 font-semibold">
                          <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                          Saving client...
                        </div>
                      </div>
                    )}
                    <div>
                      <Label className="text-sm font-medium text-slate-700">
                        NAP Box Name
                      </Label>
                      <Input
                        data-testid="napbox-name-input"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        placeholder="e.g., NAP-001"
                        className="mt-1.5 rounded-lg"
                        required
                      />
                    </div>
                    {/* <div>
                      <Label className="text-sm font-medium text-slate-700">
                        Location
                      </Label>
                      <Input
                        data-testid="napbox-location-input"
                        value={formData.location}
                        onChange={(e) =>
                          setFormData({ ...formData, location: e.target.value })
                        }
                        placeholder="e.g., Street Corner A, Subdivision B"
                        className="mt-1.5 rounded-lg"
                        required
                      />
                    </div> */}
                    <div>
                      <Label className="text-sm font-medium text-slate-700">
                        Location
                      </Label>

                      <Input
                        value={formData.location}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            location: e.target.value,
                          })
                        }
                        placeholder="Street / Address"
                        className="mt-1.5 rounded-lg"
                      />

                      <div className="mt-4">
                        <Label>Select Pin Location</Label>
                        <div className="mt-4">
                          {open && (
                            <MapPicker
                              latitude={formData.latitude}
                              longitude={formData.longitude}
                              onLocationSelect={(lat, lng) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  latitude: lat,
                                  longitude: lng,
                                }))
                              }
                              buttonLocation="True"
                              buttonGotoMap="True"
                            />
                          )}
                        </div>
                        {/* <div className="grid grid-cols-2 gap-3 mt-3 hidden"> */}
                        <div className="gap-3 mt-3 hidden">
                          <Input
                            placeholder="Latitude"
                            value={formData.latitude}
                            readOnly
                          />

                          <Input
                            placeholder="Longitude"
                            value={formData.longitude}
                            readOnly
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-slate-700">
                        Connected Server
                      </Label>
                      <Select
                        value={formData.server_id}
                        onValueChange={(val) =>
                          setFormData({ ...formData, server_id: val })
                        }
                      >
                        <SelectTrigger
                          data-testid="napbox-server-select"
                          className="mt-1.5 rounded-lg"
                        >
                          <SelectValue placeholder="Select server" />
                        </SelectTrigger>
                        <SelectContent>
                          {servers.map((server) => (
                            <SelectItem key={server.id} value={server.id}>
                              {server.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {!editingBox && (
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Total Ports
                        </Label>
                        <Select
                          value={formData.total_ports}
                          onValueChange={(val) =>
                            setFormData({ ...formData, total_ports: val })
                          }
                        >
                          <SelectTrigger
                            data-testid="napbox-ports-select"
                            className="mt-1.5 rounded-lg"
                          >
                            <SelectValue placeholder="Select port count" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="4">4 Ports</SelectItem>
                            <SelectItem value="8">8 Ports</SelectItem>
                            <SelectItem value="12">12 Ports</SelectItem>
                            <SelectItem value="16">16 Ports</SelectItem>
                            <SelectItem value="24">24 Ports</SelectItem>
                            <SelectItem value="32">32 Ports</SelectItem>
                            <SelectItem value="48">48 Ports</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-500 mt-1">
                          Ports will be auto-generated when NAP Box is created
                        </p>
                      </div>
                    )}
                    <div>
                      <Label className="text-sm font-medium text-slate-700">
                        Status
                      </Label>
                      <Select
                        value={formData.status}
                        onValueChange={(val) =>
                          setFormData({ ...formData, status: val })
                        }
                      >
                        <SelectTrigger
                          data-testid="napbox-status-select"
                          className="mt-1.5 rounded-lg"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Active">Active</SelectItem>
                          <SelectItem value="Maintenance">
                            Maintenance
                          </SelectItem>
                          <SelectItem value="Inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">
                        Description
                      </Label>
                      <Input
                        data-testid="napbox-description-input"
                        value={formData.description}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            description: e.target.value,
                          })
                        }
                        className="mt-1.5 rounded-lg"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-[20px] font-semibold h-14 mt-4"
                      data-testid="napbox-submit-btn"
                    >
                      {editingBox ? "Update NAP Box" : "Create NAP Box"}
                    </Button>
                  </div>
                </form>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search and Filters */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              placeholder="Search NAP boxes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-lg bg-white border-slate-200"
            />
          </div>
          <div className="flex gap-3 flex-wrap">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px] rounded-lg bg-white">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Maintenance">Maintenance</SelectItem>
                <SelectItem value="Inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Select value={serverFilter} onValueChange={setServerFilter}>
              <SelectTrigger className="w-[150px] rounded-lg bg-white">
                <Server className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Server" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Servers</SelectItem>
                {servers.map((server) => (
                  <SelectItem key={server.id} value={server.id}>
                    {server.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-[calc(100vh-220px)]">
          <div className="overflow-auto flex-1">
            <table className="w-full" data-testid="napboxes-table">
              <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                <tr>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider hidden sm:table-cell">
                    Location
                  </th>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider hidden md:table-cell">
                    Server
                  </th>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider hidden md:table-cell">
                    Ports
                  </th>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider hidden lg:table-cell">
                    Status
                  </th>
                  <th className="px-4 sm:px-6 py-4 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredNapBoxes.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-6 py-12 text-center text-slate-500"
                    >
                      {searchQuery ||
                      statusFilter !== "all" ||
                      serverFilter !== "all"
                        ? "No NAP boxes match your filters"
                        : "No NAP Boxes yet. Add your first NAP Box!"}
                    </td>
                  </tr>
                ) : (
                  filteredNapBoxes.map((box) => (
                    <tr
                      key={box.id}
                      data-testid={`napbox-row-${box.id}`}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-4 sm:px-6 py-4">
                        <p
                          onClick={() => viewPorts(box)}
                          className="text-sm font-semibold cursor-pointer hover:text-blue-600 text-slate-900"
                        >
                          {box.name}
                        </p>
                        {box.description && (
                          <p className="text-xs text-slate-500 mt-0.5 hidden sm:block">
                            {box.description}
                          </p>
                        )}
                        <p className="text-xs text-slate-500 sm:hidden">
                          {box.location}
                        </p>
                        <span className="text-emerald-600 font-medium sm:hidden">
                          {box.available_ports || 0} Available
                        </span>
                        <br />
                        <span className="text-blue-600 font-medium sm:hidden">
                          {box.occupied_ports || 0} Occupied
                        </span>
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-sm text-slate-600 hidden sm:table-cell">
                        {box.location}
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-sm text-slate-600 hidden md:table-cell">
                        {getServerName(box.server_id)}
                      </td>
                      <td className="px-4 sm:px-6 py-4 hidden sm:table-cell">
                        <div className="flex items-center gap-2">
                          <div className="flex flex-col text-xs">
                            <span className="text-emerald-600 font-medium">
                              {box.available_ports || 0} Available
                            </span>
                            <span className="text-blue-600 font-medium">
                              {box.occupied_ports || 0} Occupied
                            </span>
                          </div>
                          <span className="text-slate-400 text-xs">
                            / {box.total_ports || 8}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-4 hidden lg:table-cell">
                        {getStatusBadge(box.status)}
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-right">
                        <div className="flex gap-1 sm:gap-2 justify-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => viewPorts(box)}
                            className="hover:bg-emerald-50 hover:text-emerald-600 rounded-lg"
                            title="View Ports"
                            data-testid={`view-ports-${box.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(box)}
                            className="hover:bg-blue-50 hover:text-blue-600 rounded-lg"
                            data-testid={`edit-napbox-${box.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(box.id)}
                            className="hover:bg-red-50 hover:text-red-600 rounded-lg"
                            data-testid={`delete-napbox-${box.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Results count */}
        <div className="mt-4 text-sm text-slate-600">
          Showing {filteredNapBoxes.length} of {napBoxes.length} NAP boxes
        </div>

        {/* Ports Dialog */}
        <Dialog open={portsDialogOpen} onOpenChange={setPortsDialogOpen}>
          <DialogContent
            className="sm:max-w-[700px] p-0 overflow-hidden"
            data-testid="ports-dialog"
          >
            <DialogHeader className="top-0 z-20 bg-white px-6 py-4 border-b">
              <DialogTitle className="text-xl font-bold flex items-center gap-2">
                <Plug className="w-5 h-5" />
                {selectedBox?.name} - Port Management
              </DialogTitle>
            </DialogHeader>

            <div className="sm:max-h-[calc(90vh)] max-h-[calc(90vh-90px)] overflow-y-auto px-6 pb-6">
              <div className="pb-4">
                {selectedBox && (
                  <MapPicker
                    latitude={formData.latitude}
                    longitude={formData.longitude}
                    onLocationSelect={(lat, lng) =>
                      setFormData((prev) => ({
                        ...prev,
                        latitude: lat,
                        longitude: lng,
                      }))
                    }
                    buttonGotoMap="True"
                  />
                )}
              </div>
              <div className="space-y-6">
                {selectedBox && (
                  <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold text-emerald-600">
                          {ports.filter((p) => p.status === "Available").length}
                        </p>
                        <p className="text-xs text-slate-600">Available</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-blue-600">
                          {ports.filter((p) => p.status === "Occupied").length}
                        </p>
                        <p className="text-xs text-slate-600">Occupied</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-red-600">
                          {ports.filter((p) => p.status === "Faulty").length}
                        </p>
                        <p className="text-xs text-slate-600">Faulty</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {ports.map((port) => (
                    <Card
                      key={port.id}
                      className={`cursor-pointer transition-all hover:shadow-md ${
                        port.status === "Occupied"
                          ? "border-blue-200 bg-blue-50"
                          : port.status === "Faulty"
                            ? "border-red-200 bg-red-50"
                            : port.status === "Reserved"
                              ? "border-amber-200 bg-amber-50"
                              : "border-emerald-200 bg-emerald-50"
                      }`}
                      data-testid={`port-card-${port.port_number}`}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-lg font-bold text-slate-900">
                            #{port.port_number}
                          </span>
                          {getPortStatusBadge(port.status)}
                        </div>
                        {port.client_name ? (
                          <p
                            className="text-xs text-slate-700 truncate"
                            title={port.client_name}
                          >
                            {port.client_name}
                          </p>
                        ) : (
                          <p className="text-xs text-slate-400">No client</p>
                        )}

                        {port.status !== "Occupied" && (
                          <div className="mt-2">
                            <Select
                              value={port.status}
                              onValueChange={(val) =>
                                updatePortStatus(port.id, val)
                              }
                            >
                              <SelectTrigger className="h-7 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Available">
                                  Available
                                </SelectItem>
                                <SelectItem value="Reserved">
                                  Reserved
                                </SelectItem>
                                <SelectItem value="Faulty">Faulty</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {ports.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <AlertCircle className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>No ports found for this NAP Box</p>
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
